export interface Brand {
  name: string;
  primary: string;
  accent: string;
  logoUrl?: string;
  /** Where "Back to My SCG" points. Empty means the link is not rendered. */
  myScgUrl?: string;
}

export interface User {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'editor' | 'viewer';
  team: string | null;
  mustChangePassword: boolean;
  /** Reports-only director screen. */
  directorView: boolean;
}

export interface ParamDef {
  name: string;
  label?: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'daterange' | 'select' | 'multiselect' | 'lookup';
  required?: boolean;
  default?: any;
  help?: string;
  options?: { value: string; label: string }[];
  lookupTable?: string;
  lookupValueColumn?: string;
  lookupLabelColumn?: string;
  lookupFilterColumn?: string;
}

export interface RunResult {
  runId: string;
  reportId: number;
  reportSlug: string;
  reportName: string;
  reportVersion: number;
  params: Record<string, any>;
  paramLabels: { name: string; label: string; display: string }[];
  data: Record<string, Record<string, any>[]>;
  blocks: any[];
  diagnostics: {
    key: string;
    status: 'ok' | 'error' | 'skipped';
    rowCount: number;
    durationMs: number;
    cached: boolean;
    target?: string;
    error?: string;
    warnings: string[];
    accuracyNote?: string | null;
  }[];
  warnings: string[];
  accuracyNotes: string[];
  status: 'ok' | 'partial' | 'error';
  generatedAt: string;
  generatedBy: string;
  durationMs: number;
}

export { ApiError } from './errors';
import { ApiError } from './errors';
import { DEMO, demoRequest } from './demo';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Build-time constant: false in every production build, so the preview
  // transport and its recording are removed entirely by the bundler.
  if (DEMO) return demoRequest<T>(path, init);

  const res = await fetch(path, {
    // Session cookie is httpOnly and same-origin; nothing is kept in JS.
    credentials: 'same-origin',
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    const text = await res.text();
    if (!res.ok) {
      // A non-JSON error is never ours: it is Render's proxy page (502/503/504
      // while the service restarts or is unreachable) or similar. Its HTML
      // means nothing to a reader, so say what happened instead.
      const looksHtml = /^\s*</.test(text);
      const msg = [502, 503, 504].includes(res.status)
        ? 'The reporting hub is restarting or briefly unavailable. Wait a minute and try again.'
        : looksHtml
          ? `Request failed (${res.status}).`
          : text.slice(0, 300) || `Request failed (${res.status})`;
      throw new ApiError(msg, res.status);
    }
    return text as unknown as T;
  }

  const body = await res.json();
  if (!res.ok) {
    // The server always sends a human-readable `error`; surface it verbatim
    // rather than inventing a generic message.
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status, body?.detail);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export const auth = {
  me: () => api.get<{ user: User; brand: Brand }>('/api/auth/me'),
  login: (email: string, password: string) => api.post<{ user: User; brand: Brand }>('/api/auth/login', { email, password }),
  logout: () => api.post('/api/auth/logout'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: boolean }>('/api/auth/password', { currentPassword, newPassword }),
};
